import { WeekDay } from './route-optimization.interface';
import { DAY_CODE_TO_WEEKDAY } from './route-optimization.mapper';

/**
 * Fijaciones manuales de día: imponer al optimizador un día que no eligió.
 *
 * El vendedor sabe cosas que el motor no —que a ese cliente lo puede pasar el
 * martes aunque su frecuencia diga jueves—. Estas funciones deciden qué se puede
 * fijar, traducen las fijaciones al contrato y detectan cuáles rechazó el motor.
 *
 * Módulo puro: sin estado ni HTTP.
 */

/**
 * Motivos que **no** cambian por asignar otro día.
 *
 * Una coordenada que no rutea no rutea ningún día, y una ventana horaria
 * incompatible se repite igual el martes que el jueves. Ofrecer un arrastre que
 * siempre va a fallar es peor que no ofrecerlo: enseña que la función no sirve.
 *
 * Los dos motivos de coordenada entran por la misma razón que `unreachable`: un
 * punto que no puede ser real, o con latitud y longitud intercambiadas, se aparta
 * antes de calcular cualquier día. El remedio es corregir el dato del cliente, no
 * mover la visita.
 *
 * `exceeds_capacity` tampoco depende del día, pero el horizonte multi-día no lo
 * emite entre las no planificadas —no está en su `UnplannedReason`—, así que
 * incluirlo cubriría un caso que esta pantalla no recibe.
 */
export const DAY_INDEPENDENT_REASONS: ReadonlySet<string> = new Set([
  'unreachable',
  'window',
  'invalid_location',
  'swapped_location',
]);

/** ¿Asignar otro día puede cambiar el resultado de este cliente? */
export function canPinToAnotherDay(reason: string): boolean {
  return !DAY_INDEPENDENT_REASONS.has(reason);
}

/**
 * Traduce las fijaciones (`clientId → código 1..7`) al `WeekDay` del contrato.
 * Los códigos fuera de rango se descartan en vez de viajar como `undefined`.
 */
export function toWeekDayPins(pinned: ReadonlyMap<string, number>): ReadonlyMap<string, WeekDay> {
  const map = new Map<string, WeekDay>();
  pinned.forEach((code, clientId) => {
    const day = DAY_CODE_TO_WEEKDAY[code];
    if (day) map.set(clientId, day);
  });
  return map;
}

/**
 * Fijaciones que el motor **no** logró ubicar: las de clientes que volvieron a
 * quedar sin planificar.
 *
 * Se descartan en vez de reintentarse: conservar una restricción ya demostrada
 * imposible haría que cada corrida posterior la arrastre y el plan empeore sin
 * que el usuario entienda por qué.
 */
export function rejectedPins(
  pinned: ReadonlyMap<string, number>,
  unplannedIds: Iterable<string>,
): string[] {
  const unplanned = new Set(unplannedIds);
  return [...pinned.keys()].filter((id) => unplanned.has(id));
}

/** Un cliente que entra a un día: a qué día va y qué días era elegible antes. */
export interface PinnedChange {
  clientId: string;
  toDay: number;
  ownDays: number[];
}

/** Un cliente desplazado: de qué día sale y qué días era elegible. */
export interface DisplacedChange {
  clientId: string;
  fromDay: number;
  ownDays: number[];
}

/**
 * Días (códigos 1..7) cuyo conjunto de clientes elegibles cambió, y que por lo
 * tanto hay que recalcular.
 *
 * No es "el día que tocaste": asignar a un cliente al jueves también lo **saca**
 * de los días donde antes era elegible, así que esos cambian. Y desplazarlo de un
 * día lo deja disponible en los suyos, que también cambian.
 *
 * Recalcular solo estos es equivalente a recalcular todo porque el planificador
 * es greedy hacia adelante —nunca revisa un día ya resuelto— y el motor es
 * determinista.
 */
export function affectedDayCodes(
  pinned: readonly PinnedChange[],
  displaced: readonly DisplacedChange[],
): number[] {
  const days = new Set<number>();
  for (const p of pinned ?? []) {
    days.add(p.toDay);
    for (const d of p.ownDays ?? []) days.add(d);
  }
  for (const d of displaced ?? []) {
    days.add(d.fromDay);
    for (const own of d.ownDays ?? []) days.add(own);
  }
  return [...days].sort((a, b) => a - b);
}

/**
 * Rango de fechas del horizonte que cubre los días afectados.
 *
 * El rango es **contiguo** aunque los días no lo sean: el motor recibe un
 * horizonte de corrido, así que los días intermedios se recalculan de paso.
 * Pedir tramos sueltos exigiría varias llamadas y el ahorro no lo justifica.
 *
 * @param dayCodes días afectados (1..7)
 * @param horizonDates fechas del horizonte actual, en orden
 */
export function affectedDateRange(
  dayCodes: readonly number[],
  horizonDates: readonly string[],
): { start: string; end: string } | undefined {
  const wanted = new Set(dayCodes ?? []);
  const hit = (horizonDates ?? []).filter((date) => wanted.has(weekdayCodeOf(date)));
  if (!hit.length) return undefined;
  return { start: hit[0], end: hit[hit.length - 1] };
}

/** Código de día (1 = lunes … 7 = domingo) de una fecha `YYYY-MM-DD`. */
function weekdayCodeOf(date: string): number {
  const js = new Date(`${date}T00:00:00`).getDay(); // 0 = domingo
  return js === 0 ? 7 : js;
}
